import _ from 'lodash';
import React, {Component} from 'react';
import styled from 'styled-components';

import {
  isAfterAction,
  isBeforeAction,
  KeyboardActionsEnum,
} from '../../../../../libs/shared/core/src/helpers/browser/keyboardHelpers';
import {
  KeyboardActionEventHandler,
  KeyboardEventHandlers,
} from '../../../../../libs/shared/ui/src/components/keyboard/keyboardContext';
import {KeyboardShortcuts} from '../../../../../libs/shared/ui/src/components/keyboard/keyboardShortcuts';
import {
  hasSearchInInboxesParameters,
  InboxesParameters,
  isInboxesConversationsParameters,
} from '../../../../store/panels/inboxes/parameters/inboxesParameters';
import {TeamTabsDialogContainer} from '../dialogs/teamTabsDialogContainer';
import {CellsHeaderFilters, CellsHeaderFiltersProps, Filter} from './cellsHeaderFilters';

/*
 * Props.
 */

export interface CellsHeaderProps {
  parameters: InboxesParameters;
  filters: CellsHeaderFiltersProps | undefined;
}

/*
 * Style.
 */

const StyledHeaderDiv = styled.div`
  grid-row: 1;
  grid-column: 1;
  align-self: start;
  position: relative;
  min-width: 0;
  min-height: 0;
  max-height: 100%;

  display: flex;
  flex-direction: column;

  margin: 0 -1px;
  background-color: ${(p) => p.theme.foreground};
  border: solid ${(p) => p.theme.greys.shade30};
  border-width: 0 1px;
`;

const StyledTopDiv = styled.div<{$shrinkPadding?: boolean}>`
  flex-shrink: 0;

  display: grid;
  grid-template-columns: 1fr auto;
  align-items: center;

  padding: ${(p) => (p.$shrinkPadding ? 0 : '4px 8px')};

  border-bottom: 1px solid ${(p) => p.theme.greys.shade30};
`;

const StyledFiltersDiv = styled.div`
  grid-column: 1 / span 2;
  grid-row: 2;

  padding: 20px 8px;
`;

/*
 * Component.
 */

export class CellsHeader extends Component<CellsHeaderProps> {
  constructor(props: CellsHeaderProps) {
    super(props);
  }

  /*
   * Render.
   */

  render() {
    const {parameters} = this.props;
    const isInSearch = hasSearchInInboxesParameters(parameters);

    const maybeRenderTeamTabsDialog = () => {
      if (!isInboxesConversationsParameters(parameters)) {
        return null;
      }

      return <TeamTabsDialogContainer parameters={parameters} />;
    };

    return (
      <>
        {maybeRenderTeamTabsDialog()}
        {!isInSearch && (
          <StyledHeaderDiv>
            <StyledTopDiv $shrinkPadding>{this.maybeRenderFilters()}</StyledTopDiv>
          </StyledHeaderDiv>
        )}
      </>
    );
  }

  private onFilterKeyboardAction: KeyboardActionEventHandler = (e, {action}) => {
    const {filters} = this.props;
    // Do we have the new filters, or filters at all?
    if (!filters) {
      return;
    }

    const {onMultipleFiltersSelect} = filters;

    // Do we have the required method?
    if (!onMultipleFiltersSelect) {
      return;
    }

    // Grab the selected filters.
    const lastSelectedFilter = _(filters.selectedFilters)
      // Sort them in the same order as the definitions
      .sortBy((f) => filters.filters.indexOf(f))
      // Take the last one
      .last();

    if (!lastSelectedFilter) {
      return;
    }

    // Where in the definition is our last filter?
    const lastSelectedFilterIndex = filters.filters.indexOf(lastSelectedFilter);

    const selectFilter = (filter: Filter) => {
      onMultipleFiltersSelect([filter]);
    };

    if (isAfterAction(action)) {
      const nextFilterIndex = (lastSelectedFilterIndex + 1) % filters.filters.length;
      const nextFilter = filters.filters[nextFilterIndex];

      selectFilter(nextFilter);
      return;
    }

    if (isBeforeAction(action)) {
      const previousFilterIndex =
        (lastSelectedFilterIndex + filters.filters.length - 1) % filters.filters.length;
      const previousFilter = filters.filters[previousFilterIndex];

      selectFilter(previousFilter);
    }
  };

  private maybeRenderFilters() {
    const {parameters, filters} = this.props;

    const keyboardHandlers: KeyboardEventHandlers = {
      [KeyboardActionsEnum.SELECT_NEXT_TAB]: this.onFilterKeyboardAction,
      [KeyboardActionsEnum.SELECT_PREVIOUS_TAB]: this.onFilterKeyboardAction,
    };

    // Route-contextual filters.
    if (!filters) {
      return null;
    }

    return (
      <StyledFiltersDiv>
        <KeyboardShortcuts handlers={keyboardHandlers}>
          <CellsHeaderFilters {...filters} key="route" parameters={parameters} canUseMultipleFilters />
        </KeyboardShortcuts>
      </StyledFiltersDiv>
    );
  }
}
